import React from 'react';
import { Badge, Box, Flex, Text, Icon } from '@chakra-ui/react';
import { FaRegCheckCircle } from 'react-icons/fa';

import Image from '../../../tailwind/Image';
import Link from '../../../tailwind/Link';
import { formatReviewSku } from '@repo/utils';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

interface Props {
  author: string;
  body: string;
  image: object;
  location: string;
  cta?: {
    text: string;
    link: {
      linkType?: string;
      href?: string;
      internalLink?: {
        slug?: {
          current?: string;
        };
      };
    };
  };
  product: {
    href: string;
    sku: string;
  };
  title: string;
  alt?: string;
}

export default function ReviewCard({
  title,
  body,
  author,
  location,
  image,
  product,
  cta,
  alt,
  ...props
}: Readonly<Props>) {
  const { tt } = useUiComponentsInternationalContext();

  return (
    <Flex
      w="full"
      minH="32rem"
      h="full"
      flexDir="column"
      p={6}
      bg="neutral.50"
      borderRadius="lg"
      {...props}
    >
      <Box
        sx={{
          '.bv_main_container': {
            width: '100% !important',
            height: '24px !important',
            '& *': {
              fontSize: '14px !important',
            },
            '& * svg': {
              marginRight: '6px !important',
            },
          },
        }}
        mb={2}
        id={`BVRRInlineRating-${formatReviewSku(product?.sku)}`}
        data-bv-show="inline_rating"
        data-bv-product-id={formatReviewSku(product?.sku)}
        data-bv-seo="false"
      />

      <Text as="h3" textStyle="h4_5_6" noOfLines={2} minH="3.5rem" pb={6}>
        {title}
      </Text>
      <Text textStyle="caption" color="typography.light" pb={4} mb="auto">
        {body}
      </Text>
      <Flex mb={6} align="flex-end">
        <Icon as={FaRegCheckCircle} color="primary.base" mr={2} />
        <Text textStyle="caption" fontWeight="bold">
          {author || tt(s => s.reviewCardComponent.anonymousLabel)} • {location}
        </Text>
        {cta?.text && (
          <Badge
            as={Link}
            className="ml-auto text-secondary-foreground hover:bg-inverse hover:no-underline"
            link={cta?.link}
          >
            {cta?.text}
          </Badge>
        )}
      </Flex>
      <Image
        alt={alt}
        className="mb-6 rounded-lg"
        ratio={15 / 8}
        sanityImage={image}
      />

      <Link
        className="text-brand-foreground underline font-semibold"
        link={`/product/${product?.href}`}
      >
        {tt(s => s.reviewCardComponent.productLinkLabel)}
      </Link>
    </Flex>
  );
}
